/**
 * Asset Creation - Step 2: Configure
 *
 * Second step in the asset creation flow.
 * Allows user to configure asset name and attributes.
 */

import { useCallback, useEffect, useRef } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button, Stack, Text, Title } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import type { AssetCreationResult } from '@/features/assets/context/AssetCreationContext'
import type { Id } from 'convex/_generated/dataModel'
import { useAssetCreation } from '@/features/assets/context/AssetCreationContext'
import { useAssetMutations } from '@/features/assets/hooks/useAssetMutations'
import { useAssetAnalytics } from '@/features/assets/hooks/useAssetAnalytics'
import { AssetConfigureStep } from '@/features/assets/components/AssetConfigureStep'
import { useWorkflowNavigation } from '@/components/common'

export const Route = createFileRoute('/_authed/assets/new/configure')({
  component: ConfigureStep,
})

function ConfigureStep() {
  const navigate = useNavigate()
  const {
    selectedCatalog,
    customName,
    attributes,
    parentId,
    isSubmitting,
    setCustomName,
    setAttributes,
    setIsSubmitting,
    setCreationResult,
  } = useAssetCreation()
  const { updatePrimaryButton, updateSecondaryButton } = useWorkflowNavigation()

  const { createAsset } = useAssetMutations()
  const { trackAssetCreated } = useAssetAnalytics()

  const handleBack = useCallback(() => {
    navigate({ to: '/assets/new', search: { parentId: parentId as string | undefined } })
  }, [parentId, navigate])

  const handleSubmit = useCallback(async () => {
    if (!selectedCatalog) return

    setIsSubmitting(true)
    try {
      const assetId = await createAsset({
        assetCatalogId: selectedCatalog._id,
        customName: customName || undefined,
        attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
        parentInventoryId: parentId || undefined,
      })

      trackAssetCreated({
        assetType: selectedCatalog.name,
        assetCategory: selectedCatalog.category,
        hasParent: !!parentId,
        attributeKeys: Object.keys(attributes),
      })

      // Set the creation result
      const result: AssetCreationResult = {
        assetId: assetId as Id<'assetInventory'>,
        assetName: customName || selectedCatalog.name,
        catalogItem: selectedCatalog,
        attributes,
        queueItemsCreated: 0,
        childrenCreated: 0,
      }
      setCreationResult(result)

      // Navigate to complete step
      navigate({ to: '/assets/new/complete', search: { parentId: parentId as string | undefined } })
    } catch (error) {
      console.error('Failed to create asset:', error)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    selectedCatalog,
    customName,
    attributes,
    parentId,
    createAsset,
    trackAssetCreated,
    setCreationResult,
    setIsSubmitting,
    navigate,
  ])

  // Use refs to store latest callbacks to avoid infinite loops
  // The callbacks depend on frequently-changing values (attributes, customName, etc.)
  // so including them in useEffect deps would cause infinite re-renders
  const handleBackRef = useRef(handleBack)
  const handleSubmitRef = useRef(handleSubmit)
  handleBackRef.current = handleBack
  handleSubmitRef.current = handleSubmit

  // Configure navigation buttons
  // Only include isSubmitting in deps since that's what we need to react to
  useEffect(() => {
    updateSecondaryButton({
      label: 'Back',
      onClick: () => handleBackRef.current(),
      variant: 'subtle',
    })

    updatePrimaryButton({
      label: 'Create Asset',
      onClick: () => handleSubmitRef.current(),
      loading: isSubmitting,
      leftSection: <IconCheck size={18} />,
      disabled: isSubmitting,
    })

    return () => {
      updateSecondaryButton(undefined)
      updatePrimaryButton(undefined)
    }
  }, [isSubmitting, updatePrimaryButton, updateSecondaryButton])

  // Redirect to first step if no catalog selected
  if (!selectedCatalog) {
    return (
      <Stack gap="md">
        <Title order={3}>No asset type selected</Title>
        <Text c="dimmed">Please go back and select an asset type.</Text>
        <Button
          onClick={() =>
            navigate({ to: '/assets/new', search: { parentId: parentId as string | undefined } })
          }
        >
          Go Back
        </Button>
      </Stack>
    )
  }

  return (
    <AssetConfigureStep
      catalogItem={selectedCatalog}
      customName={customName}
      attributes={attributes}
      onNameChange={setCustomName}
      onAttributesChange={setAttributes}
      showSuggestedProjectPreview={true}
    />
  )
}
